/**
 * The "Billed to" row at the top of a customer's payment area (docs/features/
 * credit-control/20_billed_to_update_spec.md): which customer pays for this
 * customer's CIS orders, for every customer, whoever masters its record.
 *
 * - "This customer pays for itself", or "Billed to Bloggs & Co (CUS-…)" with a
 *   link to the payer's Settings;
 * - who last changed it, when and why, once it has been changed in CIS;
 * - "Change…" opens the dialog (`BilledToDialog`), offered only where the read
 *   carries the URL: finance, managers and admins, and never for Pollards'
 *   internal account. Everyone else reads the row as it is.
 *
 * The row owns the call. A refusal is bound to the dialog; a save reads the
 * page's credit view again, so the payment area follows (the payer's summary
 * for a sub-account, the customer's own settings when it pays for itself).
 */
import { useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { extractFieldErrors } from "@shared-core/data/validation";
import type {
  CustomerCreditView,
  SetBilledToInput,
} from "@shared-core/data/credit-control-types";
import { formatDateTimeOrDash } from "@shared-core/domain/formatters";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../primitives/button";
import { BilledToDialog, type BilledToError } from "./BilledToDialog";

/** A refused save, bound to the dialog's inputs: the payer under the search, the reason under the reason. */
function billedToError(err: unknown): BilledToError {
  const fieldErrors = extractFieldErrors(err);
  const messageFor = (field: string) =>
    fieldErrors
      .filter((e) => e.field === field)
      .map((e) => e.message)
      .join(" ") || null;
  const payerMessage = messageFor("payer_customer_code");
  const reasonMessage = messageFor("reason");
  return {
    payerMessage,
    reasonMessage,
    message:
      payerMessage || reasonMessage
        ? null
        : mutationErrorMessage(
            err,
            "Could not change who this customer is billed to.",
          ),
  };
}

export function BilledToRow({
  credit,
  onChanged,
}: {
  credit: CustomerCreditView;
  /** Billed to was changed: read the credit view again. */
  onChanged: () => Promise<void>;
}) {
  const adapter = useDataAdapter();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<BilledToError | null>(null);
  const { payer, billingAccount: account, billedToLastChange: last } = credit;
  // A sub-account's account is its payer's: the payer's page is the account's.
  const payerSettings =
    payer &&
    paths.customerSettings({
      customerId: account?.sourceCustomerCode ?? payer.customerCode,
    });

  const save = async (input: SetBilledToInput) => {
    setSaving(true);
    setError(null);
    try {
      await adapter.setBilledTo(credit, input);
      setOpen(false);
      await onChanged();
    } catch (err) {
      setError(billedToError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      aria-label="Billed to"
      className="space-y-1 rounded-lg border border-border p-4 text-sm"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <p className="min-w-0 flex-1 font-medium">
          {payer && payerSettings ? (
            <>
              Billed to{" "}
              <Link
                to={payerSettings}
                className="text-primary underline-offset-2 hover:underline"
              >
                {payer.customerName}
              </Link>{" "}
              ({payer.customerCode})
            </>
          ) : (
            "This customer pays for itself"
          )}
        </p>
        {credit.billedToUrl && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              setError(null);
              setOpen(true);
            }}
          >
            Change…
          </Button>
        )}
      </div>
      {last && (
        <p className="text-xs text-muted-foreground">
          Changed by {last.byName ?? "a former user"} on{" "}
          {formatDateTimeOrDash(last.at)}: “{last.reason}”
        </p>
      )}
      {credit.billedToUrl && (
        <BilledToDialog
          open={open}
          onOpenChange={setOpen}
          credit={credit}
          error={error}
          saving={saving}
          onConfirm={(input) => void save(input)}
        />
      )}
    </section>
  );
}
